import type { Plugin } from "../../../core/plugin";
import { PluginRegistry } from "../../../core/pluginRegistry";

/**
 * ltnAccordion — acordeón de "Servicios" para la home de LTN (LTNW-1).
 *
 * Sustituye el `state.open` + `mountAcc()` de React del prototipo, que
 * reconstruía toda la lista en cada clic. Aquí los doce paneles se renderizan
 * en servidor, así que el copy está en el HTML para los crawlers y es legible
 * sin JavaScript; este plugin solo alterna `hidden` y `aria-expanded`.
 *
 * Un panel abierto a la vez, y hacer clic en el que está abierto lo cierra —
 * mismo comportamiento que el prototipo.
 *
 * Contrato de marcado:
 *   <div data-plugin="ltnAccordion">
 *     <button data-ltn-acc-trigger aria-expanded aria-controls="panel-id">
 *     <div id="panel-id" data-ltn-acc-panel hidden>
 */
export class LtnAccordion implements Plugin {
  private triggers: HTMLButtonElement[] = [];

  private readonly onClick = (event: MouseEvent) => {
    const trigger = (event.target as HTMLElement).closest<HTMLButtonElement>(
      "[data-ltn-acc-trigger]",
    );
    if (!trigger || !this.element.contains(trigger)) return;

    const wasOpen = trigger.getAttribute("aria-expanded") === "true";
    this.closeAll();
    if (!wasOpen) this.setOpen(trigger, true);
  };

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.triggers = [
      ...this.element.querySelectorAll<HTMLButtonElement>(
        "[data-ltn-acc-trigger]",
      ),
    ];
    this.element.addEventListener("click", this.onClick);
  }

  destroy(): void {
    this.element.removeEventListener("click", this.onClick);
  }

  private panelFor(trigger: HTMLElement): HTMLElement | null {
    const id = trigger.getAttribute("aria-controls");
    return id
      ? this.element.querySelector<HTMLElement>(`#${CSS.escape(id)}`)
      : null;
  }

  private setOpen(trigger: HTMLElement, open: boolean): void {
    trigger.setAttribute("aria-expanded", String(open));
    this.panelFor(trigger)?.toggleAttribute("hidden", !open);
  }

  private closeAll(): void {
    for (const trigger of this.triggers) this.setOpen(trigger, false);
  }
}

PluginRegistry.register("ltnAccordion", LtnAccordion);
